import FunnelLayout from "@/v2/components/FunnelLayout";
import PrimaryCTA from "@/v2/components/PrimaryCTA";
import { CheckCircle2, ChevronRight, Lightbulb, RefreshCw, Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import React from "react";
import federicoImage from "@/assets/federico-piccinini.png.asset.json";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const trustItems = [
  "7 giorni, non 7 mesi",
  "Metodo guidato step-by-step",
  "Verdetto su dati reali: continua, itera o fermati",
];

const steps = [
  {
    n: "01",
    title: "Reality Check",
    body: "Rispondi a 7 domande. Ricevi verdetto, rischio principale e l'esperimento da fare subito.",
  },
  {
    n: "02",
    title: "Esegui",
    body: "Costruisci la landing con AI in un giorno. Porta traffico reale. Raccogli dati concreti in 7 giorni.",
  },
  {
    n: "03",
    title: "Decidi",
    body: "Continua, itera o fermati — basandoti su dati reali, non su supposizioni.",
  },
];

const perTeSeItems = [
  {
    Icon: Lightbulb,
    text: "Hai un'idea ma non sai se qualcuno la pagherebbe davvero",
  },
  {
    Icon: RefreshCw,
    text: "Hai già provato a lanciare qualcosa e non ha funzionato come speravi",
  },
  {
    Icon: Rocket,
    text: "Stai costruendo un prodotto ma non hai ancora un cliente pagante",
  },
];

const viewportOnce = { once: true, margin: "-60px" } as const;

export default function Landing() {
  return (
    <FunnelLayout>
      <Helmet>
        <title>Proof | Progetto Startup</title>
        <meta name="description" content="Testa la tua idea imprenditoriale in 7 giorni con dati reali. Reality Check gratuito + Sprint guidato con AI." />
        <link rel="canonical" href="https://progettostartup.com/" />
        <meta property="og:title" content="Proof | Progetto Startup" />
        <meta property="og:description" content="Testa la tua idea imprenditoriale in 7 giorni con dati reali. Reality Check gratuito + Sprint guidato con AI." />
        <meta property="og:url" content="https://progettostartup.com/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Proof | Progetto Startup" />
        <meta name="twitter:description" content="Testa la tua idea imprenditoriale in 7 giorni con dati reali. Reality Check gratuito + Sprint guidato con AI." />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Quanto costa?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Il Reality Check è completamente gratuito. Il percorso completo è a pagamento: vedi la pagina prezzi.",
                },
              },
              {
                "@type": "Question",
                name: "Quanto tempo richiede?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "7 giorni, circa 30–60 minuti al giorno. Il Reality Check iniziale richiede 5 minuti.",
                },
              },
              {
                "@type": "Question",
                name: "Serve già avere un'idea?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Sì, serve un'idea o un problema che vuoi esplorare. Non serve un prodotto già costruito — anzi, prima testi, meglio è.",
                },
              },
              {
                "@type": "Question",
                name: "Cosa succede se non raggiungo la soglia?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Ottieni comunque dati reali su cosa non ha funzionato — e un consiglio specifico su cosa cambiare. Spesso è il risultato più utile.",
                },
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Proof Sprint",
            serviceType: "Validazione idea di startup in 7 giorni",
            description:
              "Percorso guidato di 7 giorni per testare un'idea di startup con dati reali: ipotesi, esperimento, raccolta evidenze e verdetto.",
            areaServed: "IT",
            provider: { "@type": "Organization", name: "Progetto Startup", url: "https://progettostartup.com/" },
            url: "https://progettostartup.com/v2/pricing",
          })}
        </script>
      </Helmet>


      {/* HERO — una promessa, una CTA, un trust signal */}
      <motion.section
        className="relative max-w-3xl mx-auto text-center space-y-7"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={stagger}
      >
        {/* glow decorativo */}
        <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center">
          <div className="h-[420px] w-[420px] rounded-full bg-[#FF6B47]/10 blur-[96px]" />
        </div>

        <motion.h1 variants={fadeUp} className="font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]">
          Testa prima.{" "}
          <span className="bg-gradient-to-r from-[#FF6B47] to-[#FF8A6B] bg-clip-text text-transparent">
            Costruisci dopo.
          </span>
        </motion.h1>

        <motion.p variants={fadeUp} className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
          La maggior parte dei founder passa mesi a costruire prima di scoprire che nessuno pagherà. Proof ti dà la risposta in 7 giorni con dati reali, non supposizioni.
        </motion.p>

        <motion.div variants={fadeUp} className="flex flex-col items-center gap-3 pt-2">
          <motion.div whileHover={{ scale: 1.025 }} whileTap={{ scale: 0.98 }}>
            <PrimaryCTA to="/v2/reality-check" variant="lg">
              Scopri se la tua idea regge
            </PrimaryCTA>
          </motion.div>
          <p className="text-sm text-slate-400">
            Reality Check gratis · ~5 minuti · nessuna carta
          </p>
          <p className="text-xs text-slate-600 mt-1">
            Nessuno spam. Mai.{" "}
            <Link to="/privacy-policy" className="underline underline-offset-2 hover:text-slate-400 transition-colors">
              Privacy policy
            </Link>
          </p>
        </motion.div>

        {/* Trust strip */}
        <motion.ul variants={fadeUp} className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-4 text-sm text-slate-300">
          {trustItems.map((t) => (
            <li key={t} className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-secondary shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </motion.ul>
      </motion.section>

      {/* COME FUNZIONA — sezione secondaria, numerata, peso visivo ridotto */}
      <motion.section
        className="mt-24 max-w-4xl mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={stagger}
      >
        <motion.h2 variants={fadeUp} className="font-display text-2xl md:text-3xl font-semibold text-center mb-10">
          Come funziona
        </motion.h2>
        <div className="flex flex-col md:flex-row items-stretch gap-4">
          {steps.map((s, i) => (
            <React.Fragment key={s.n}>
              <motion.div
                variants={fadeUp}
                className="flex-1 relative rounded-xl border border-white/10 bg-white/[0.03] p-5 overflow-hidden hover:border-white/20 hover:shadow-lg hover:shadow-[#FF6B47]/5 hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#FF6B47]/60 to-transparent" />
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#FF6B47]/15 border border-[#FF6B47]/30 text-[#FF6B47] text-sm font-bold mb-4">
                  {i + 1}
                </span>
                <h3 className="font-display font-semibold text-base mb-1.5">{s.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.body}</p>
              </motion.div>
              {i < steps.length - 1 && (
                <div className="hidden md:flex items-center justify-center text-slate-600 shrink-0 px-1">
                  <ChevronRight className="h-5 w-5" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </motion.section>

      {/* CTA INTERMEDIA */}
      <motion.section
        className="mt-16 max-w-xl mx-auto text-center"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={stagger}
      >
        <motion.div variants={fadeUp}>
          <PrimaryCTA to="/v2/reality-check">
            Fai il Reality Check gratis
          </PrimaryCTA>
        </motion.div>
      </motion.section>

      {/* PER CHI È PROOF */}
      <motion.section
        className="mt-24 max-w-3xl mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={stagger}
      >
        <motion.h2 variants={fadeUp} className="font-display text-2xl md:text-3xl font-semibold text-center mb-10">
          Proof è per te se…
        </motion.h2>
        <ul className="grid md:grid-cols-3 gap-4">
          {perTeSeItems.map(({ Icon, text }) => (
            <motion.li
              variants={fadeUp}
              key={text}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-5 hover:border-white/20 hover:shadow-lg hover:shadow-[#FF6B47]/5 hover:-translate-y-0.5 transition-all duration-200"
            >
              <Icon className="h-5 w-5 text-[#FF6B47]/70 mb-3" />
              <p className="text-sm text-slate-300 leading-relaxed">{text}</p>
            </motion.li>
          ))}
        </ul>
      </motion.section>


      {/* CHI SONO */}
      <motion.section
        className="mt-24 max-w-2xl mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={stagger}
      >
        <motion.p variants={fadeUp} className="text-center text-sm text-slate-200 uppercase tracking-widest mb-6">
          Chi c'è dietro Proof
        </motion.p>
        <motion.div variants={fadeUp} className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-white/[0.02] p-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-8">
            <div className="shrink-0">
              <img
                src={federicoImage.url}
                alt="Federico Piccinini"
                className="w-28 h-28 rounded-2xl object-cover border border-white/10"
              />
            </div>
            <div className="text-center sm:text-left flex-1">
              <p className="text-xl font-semibold text-slate-100 mb-0.5">Federico Piccinini</p>
              <p className="text-xs text-[#FF6B47] mb-4 uppercase tracking-wider font-medium">Founder · Progetto Startup</p>
              <p className="text-sm text-slate-300 leading-relaxed mb-5">
                Ho affiancato 300+ founder nella fase più difficile: quella in cui l'idea c'è, ma non si sa se ha senso. Ho costruito Proof perché il problema non è avere idee, è capire subito quali meritano il tuo tempo.
              </p>
              <div className="flex items-center gap-4 justify-center sm:justify-start">
                <Link to="/about" className="text-sm text-slate-400 hover:text-slate-200 underline underline-offset-2 transition-colors">
                  Chi sono →
                </Link>
                <a href="https://www.linkedin.com/in/federico-piccinini" target="_blank" rel="noopener noreferrer"
                  className="text-sm text-slate-400 hover:text-slate-200 underline underline-offset-2 transition-colors">
                  LinkedIn →
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.section>

      <motion.section
        className="mt-24 max-w-2xl mx-auto space-y-6"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="text-center">
          <h2 className="font-display text-2xl md:text-3xl font-semibold">
            Ecco come inizia
          </h2>
          <p className="text-lg text-slate-200 font-medium mt-2 mb-8">
            In 5 minuti sai già se vale la pena andare avanti.
          </p>
        </motion.div>

        <motion.div variants={fadeUp} className="rounded-2xl border border-amber-400/20 bg-white/[0.04] p-5 md:p-6 space-y-5 hover:border-amber-400/40 hover:shadow-lg hover:shadow-[#FF6B47]/5 hover:-translate-y-0.5 transition-all duration-200">
          <div className="flex items-start justify-between gap-4">
            <p className="text-[10px] uppercase tracking-widest text-amber-400">
              Output del Reality Check
            </p>
            <span className="text-xs bg-white/10 text-slate-400 rounded-full px-2.5 py-0.5">
              Esempio
            </span>
          </div>

          <div className="rounded-xl bg-white/5 px-4 py-3 space-y-0.5">
            <p className="text-xs text-slate-400">
              <span className="text-slate-500 uppercase tracking-wider text-[10px]">Idea · </span>
              App per gestire prenotazioni e turni in piccoli ristoranti
            </p>
            <p className="text-xs text-slate-400">
              <span className="text-slate-500 uppercase tracking-wider text-[10px]">Target · </span>
              Titolari di ristoranti e bar con 5–20 dipendenti
            </p>
          </div>

          {/* Verdetto */}
          <div className="border-l-2 border-amber-400/40 pl-4">
            <p className="text-[10px] uppercase tracking-widest text-amber-400">
              Verdetto
            </p>
            <p className="text-xl font-bold text-amber-400 tracking-widest mt-1">
              ITERA
            </p>
            <p className="text-sm text-slate-300 mt-2">
              Il problema è reale ma non ci sono ancora prove che il target sia disposto a cambiare le proprie abitudini per risolverlo.
            </p>
          </div>

          <hr className="border-white/10" />

          {/* Esperimento consigliato */}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-secondary">
              Esperimento consigliato
            </p>
            <p className="font-medium text-white text-sm mt-2">
              Landing page + lista d'attesa
            </p>
            <p className="text-sm text-slate-400 mt-1">
              Obiettivo: 20 iscritti in 7 giorni. Se ci arrivi, il mercato ha risposto. Se no, il problema non è abbastanza urgente.
            </p>
          </div>
        </motion.div>

        {/* Bridge verso Sprint */}
        <motion.div variants={fadeUp} className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-center space-y-2 hover:border-white/20 hover:shadow-lg hover:shadow-[#FF6B47]/5 hover:-translate-y-0.5 transition-all duration-200">
          <p className="text-sm text-slate-300 leading-relaxed">
            Poi inizia il tuo <span className="text-white font-medium">percorso Proof di 7 giorni</span>: costruisci la landing con AI, porti traffico reale, raccogli dati. Verdetto finale su dati concreti.
          </p>
        </motion.div>

        <motion.div variants={fadeUp} className="text-center space-y-4 pt-2">
          <p className="text-sm text-slate-400">
            Il Reality Check è gratuito e richiede 5 minuti. Nessuna carta richiesta.
          </p>
          <PrimaryCTA to="/v2/reality-check">
            Fai il Reality Check gratis
          </PrimaryCTA>
        </motion.div>
      </motion.section>

      {/* FAQ */}
      <motion.section
        className="mt-24 max-w-2xl mx-auto pb-8"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={stagger}
      >
        <motion.h2 variants={fadeUp} className="font-display text-2xl md:text-3xl font-semibold text-center mb-8">
          Domande frequenti
        </motion.h2>
        <ul className="space-y-4">
          {[
            {
              q: "Quanto costa?",
              a: (
                <>
                  Il Reality Check è completamente gratuito.{" "}
                  <Link
                    to="/v2/pricing"
                    className="text-slate-200 underline underline-offset-2 hover:text-white transition-colors"
                  >
                    Vedi i prezzi per il percorso completo →
                  </Link>
                </>
              ),
            },
            {
              q: "Quanto tempo richiede?",
              a: "7 giorni, circa 30–60 minuti al giorno. Il Reality Check iniziale richiede 5 minuti.",
            },
            {
              q: "Serve già avere un'idea?",
              a: "Sì, serve un'idea o un problema che vuoi esplorare. Non serve un prodotto già costruito — anzi, prima testi, meglio è.",
            },
            {
              q: "Cosa succede se non raggiungo la soglia?",
              a: "Ottieni comunque dati reali su cosa non ha funzionato — e un consiglio specifico su cosa cambiare. Spesso è il risultato più utile.",
            },
          ].map((item) => (
            <motion.li
              variants={fadeUp}
              key={item.q}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-5 hover:border-white/20 hover:shadow-lg hover:shadow-[#FF6B47]/5 hover:-translate-y-0.5 transition-all duration-200"
            >
              <p className="font-medium text-slate-100 mb-2">{item.q}</p>
              <p className="text-sm text-slate-400 leading-relaxed">{item.a}</p>
            </motion.li>
          ))}
        </ul>

        <motion.div variants={fadeUp} className="mt-10 text-center">
          <PrimaryCTA to="/v2/reality-check">
            Fai il Reality Check gratis
          </PrimaryCTA>
          <p className="text-xs text-slate-500 mt-3">~5 minuti · nessuna carta richiesta</p>
          <p className="text-xs text-slate-500 mt-1">
            Il Reality Check è gratuito. Paghi solo se vuoi continuare con il percorso completo.
          </p>
        </motion.div>
      </motion.section>

      {/* Glow decorativo inferiore */}
      <div className="pointer-events-none fixed bottom-0 left-1/2 -translate-x-1/2 -z-10">
        <div className="h-[320px] w-[640px] rounded-full bg-violet-600/5 blur-[120px]" />
      </div>
    </FunnelLayout>
  );
}
